import React, { useEffect, useState } from 'react';
import AdminDashboard from 'layouts/AdminDashboard';
import {
  Typography,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Link,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from '@mui/material';
import {
  getAdminMembershipsAndSubscriptions,
  getAllUserAssignments,
  getAdminStats,
  AdminStats,
} from './DashboardService';
import { listTrials, TrialRecord } from '../Trials/TrialAdminService';
import { useNavigate } from 'react-router-dom';
import Container from 'components/Container';
import { notify } from 'utils/notify';
import { extractErrorMessage } from 'utils/extractErrorMessage';

const DashboardList = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [adminMemberships, setAdminMemberships] = useState<any[]>([]);
  const [userAssignments, setUserAssignments] = useState<any[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [expiringTrials, setExpiringTrials] = useState<TrialRecord[]>([]);

  useEffect(() => {
    listAdminMembershipsAndSubscriptions();
    listUserAssignments();
    getAdminStats()
      .then((r) => setStats(r.data))
      .catch(() => { /* stats are optional — don't block the page */ });
    listTrials(0, 10, 'trialing', 7)
      .then((r) => setExpiringTrials(r.data.content))
      .catch(() => { /* expiring trials are optional — don't block the page */ });
  }, []);

  function listAdminMembershipsAndSubscriptions(): void {
    setLoading(true);
    getAdminMembershipsAndSubscriptions()
      .then((response) => {
        setAdminMemberships(response.data);
      })
      .catch((error) => {
        console.error(error);
        notify(extractErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function listUserAssignments(): void {
    setLoading(true);
    getAllUserAssignments()
      .then((response) => {
        setUserAssignments(response.data);
      })
      .catch((error) => {
        console.error(error);
        notify(extractErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
      });
  }

  // Navigate to the SubscriptionDetail page when a subscription row is clicked
  function selectSubscription(membershipId: string, subscriptionId: string) {
    navigate(
      `/admin/dashboard/membership/${membershipId}/subscription/${subscriptionId}`,
    );
  }

  function selectUserAssignment(assignmentId: string) {
    navigate(`/admin/dashboard/assignment/${assignmentId}`);
  }

  return (
    <Container>
      <AdminDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              Account Admin
            </Link>
            <Typography color="text.primary">Dashboard</Typography>
          </Breadcrumbs>
        </Box>

        {/* Platform-wide summary stats */}
        {stats && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
              gap: 2,
              mb: 4,
            }}
          >
            {[
              { label: 'Total Users', value: stats.totalUsers },
              { label: 'Total Subscriptions', value: stats.totalSubscriptions },
              { label: 'Active Trials', value: stats.activeTrials },
              { label: 'Published Plans', value: stats.publishedPlans },
              { label: 'Published Templates', value: stats.publishedTemplates },
            ].map(({ label, value }) => (
              <Paper
                key={label}
                sx={{ p: 2.5, textAlign: 'center', borderRadius: 2 }}
              >
                <Typography variant="h4" fontWeight={700}>
                  {value}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {label}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* Trials Ending Soon */}
        {expiringTrials.length > 0 && (
          <Box mb={4}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6" fontWeight={700}>
                Trials Ending Within 7 Days
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate('/admin/trials')}
              >
                View All Trials
              </Button>
            </Box>
            <Paper sx={{ borderRadius: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['User', 'Email', 'Tier', 'Ends', 'Days Left', ''].map((h) => (
                        <TableCell key={h}>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase' }}
                          >
                            {h}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expiringTrials.map((t) => {
                      const daysLeft = t.trialEndDate
                        ? Math.ceil(
                          (new Date(t.trialEndDate).getTime() - Date.now()) /
                              86_400_000,
                        )
                        : null;
                      const urgency =
                        daysLeft !== null && daysLeft <= 2
                          ? 'error'
                          : daysLeft !== null && daysLeft <= 5
                            ? 'warning'
                            : 'default';
                      return (
                        <TableRow
                          key={t.subscriptionId}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate('/admin/trials')}
                        >
                          <TableCell>{t.userName?.trim() || '—'}</TableCell>
                          <TableCell>{t.userEmail}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>
                            {t.tier}
                          </TableCell>
                          <TableCell>
                            {t.trialEndDate
                              ? new Date(t.trialEndDate).toLocaleDateString(
                                'en-GB',
                                { day: '2-digit', month: 'short', year: 'numeric' },
                              )
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {daysLeft !== null ? (
                              <Chip
                                label={`${daysLeft}d`}
                                color={urgency}
                                size="small"
                                variant={urgency === 'default' ? 'outlined' : 'filled'}
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/admin/trials');
                              }}
                            >
                              Manage
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}

        {/* Memberships and Subscriptions */}
        <Box marginBottom={4}>
          <Typography variant="h4" gutterBottom>
            Memberships, Subscriptions, and Assignments
          </Typography>

          {loading && <Typography>Loading...</Typography>}

          {!loading &&
            adminMemberships?.map((membership, index) => (
              <Box key={membership?.membership?.id || index} marginBottom={4}>
                {/* Membership Info */}
                <Typography variant="h5" gutterBottom>
                  Membership:{' '}
                  {membership?.membership?.membershipName || 'Unknown'} (ID:{' '}
                  {membership?.membership?.id || 'N/A'})
                </Typography>
                <Typography variant="subtitle1">
                  Description:{' '}
                  {membership?.membership?.description ||
                    'No description available'}
                </Typography>

                {/* Subscriptions */}
                <Box marginY={2}>
                  <Typography variant="h6">Subscriptions</Typography>
                  <TableContainer component={Paper}>
                    <Table
                      sx={{ minWidth: 750 }}
                      aria-label="subscriptions table"
                    >
                      <TableHead sx={{ bgcolor: 'alternate.main' }}>
                        <TableRow>
                          <TableCell>
                            <Typography
                              color={'text.secondary'}
                              variant={'caption'}
                              fontWeight={700}
                              sx={{ textTransform: 'uppercase' }}
                            >
                              Subscription ID
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              color={'text.secondary'}
                              variant={'caption'}
                              fontWeight={700}
                              sx={{ textTransform: 'uppercase' }}
                            >
                              Subscription Name
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              color={'text.secondary'}
                              variant={'caption'}
                              fontWeight={700}
                              sx={{ textTransform: 'uppercase' }}
                            >
                              Status
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              color={'text.secondary'}
                              variant={'caption'}
                              fontWeight={700}
                              sx={{ textTransform: 'uppercase' }}
                            >
                              Onboarding Status
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {membership?.subscriptions?.map((subscription) => (
                          <TableRow
                            key={subscription?.id}
                            onClick={() =>
                              selectSubscription(
                                membership?.membership?.id,
                                subscription?.id,
                              )
                            } // Navigate to subscription detail page
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{subscription?.id || 'N/A'}</TableCell>
                            <TableCell>
                              {subscription?.subscriptionName ||
                                'Unnamed Subscription'}
                            </TableCell>
                            <TableCell>
                              {subscription?.status || 'Unknown'}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant={'caption'}
                                fontWeight={700}
                                sx={{
                                  bgcolor:
                                    subscription?.onboardingStatus ===
                                    'Complete'
                                      ? (theme) => theme.palette.success.light
                                      : (theme) => theme.palette.warning.light, // Orange for other statuses
                                  color:
                                    subscription?.onboardingStatus ===
                                    'Complete'
                                      ? '#FFFFFF'
                                      : '#FFFFFF', // White font for non-complete statuses
                                  paddingX: 1.5,
                                  paddingY: 0.5,
                                  borderRadius: 2,
                                  display: 'inline-block',
                                }}
                              >
                                {subscription?.onboardingStatus || 'Unknown'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            ))}

          {/* Assignments (from global `userAssignments`) */}
          <Box marginY={2}>
            <Typography variant="h6">Assignments</Typography>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 750 }} aria-label="assignments table">
                <TableHead sx={{ bgcolor: 'alternate.main' }}>
                  <TableRow>
                    <TableCell>
                      <Typography
                        color={'text.secondary'}
                        variant={'caption'}
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase' }}
                      >
                        Assignment ID
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color={'text.secondary'}
                        variant={'caption'}
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase' }}
                      >
                        Task
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color={'text.secondary'}
                        variant={'caption'}
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase' }}
                      >
                        Description
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        color={'text.secondary'}
                        variant={'caption'}
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase' }}
                      >
                        Status
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userAssignments.map((assignment) => (
                    <TableRow
                      key={assignment?.id}
                      onClick={() => selectUserAssignment(assignment?.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{assignment?.id || 'N/A'}</TableCell>
                      <TableCell>
                        {assignment?.name || 'Unnamed Task'}
                      </TableCell>
                      <TableCell>
                        {assignment?.description || 'No description'}
                      </TableCell>
                      <TableCell>{assignment?.status || 'No status'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </AdminDashboard>
    </Container>
  );
};

export default DashboardList;
